import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClub } from '../../club/ClubProvider.tsx'
import { useAreas } from '../../data/geo.ts'
import { frameFor } from '../../data/lookGeometry.ts'
import { lookForChoice, type PlanJob, type TaskTypeRow } from '../../data/plan.ts'
import { buildHistory } from '../../data/settingsHistory.ts'
import { applyParams, leaveKey, proposeSettings, ruleSettings, ruleStepKeys, staleParams, type SettingProposal } from '../../data/settingsRule.ts'
import { Banner, PendingBanner } from '../../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../../ui/hooks.ts'
import { LookPicture } from '../../ui/looks/LookPicture.tsx'
import { asideText, plannedUntil, readText, reasonText, type T } from './settingsText.ts'
import { useReadsWork, useSettingsData, useToday } from './useSettingsData.ts'

/** A number the planner typed: whole, at least 1; empty, zero or anything else is no number (nothing is skipped by default). */
export const limitOf = (text: string): number | null => (/^\s*\d{1,3}\s*$/.test(text) && Number(text) >= 1 ? Number(text) : null)

/** M25-A (docs/M25.md D, "Where it acts"; ADR-131): « Proposer les réglages » on a planned job — the next direction, lap and groomers for the job's areas from what
 *  was really done on them and what is already planned before that day, with its reasons and the rule's steps printed word for word. A RULE, never a model:
 *  `data/settingsRule.ts`. Nothing is saved until « Appliquer » sends ONE ordinary `job.update@1` (the job's params with the ticked values, `expected_revision`,
 *  `sendAndWait`, `PendingBanner`); the job becomes hand-edited, which the sentence above the button says. Shown only where the viewer holds `job.plan` and
 *  `worklog.read` and is not on a workstation (rule 2: nowhere else is the history on the device); the page keeps nothing (the two numbers, the choices left out
 *  and the ticks are page state). */
export function SettingsPanel({ job, tt, onOpenForm }: { job: PlanJob; tt: TaskTypeRow | undefined; onOpenForm?: () => void }) {
  const { t, i18n } = useTranslation()
  const reads = useReadsWork()
  const [open, setOpen] = useState(false)
  const settings = useMemo(() => ruleSettings(tt, i18n.language), [tt, i18n.language])
  if (!reads || settings.length === 0) return null
  if (!open) return <button type="button" className="btn self-start" onClick={() => setOpen(true)} data-testid="settings-open">{t('plan.settingsPanel.open')}</button>
  return <PanelBody job={job} tt={tt} onOpenForm={onOpenForm} onClose={() => setOpen(false)} />
}

function PanelBody({ job, tt, onOpenForm, onClose }: { job: PlanJob; tt: TaskTypeRow | undefined; onOpenForm?: () => void; onClose(): void }) {
  const { t, i18n } = useTranslation()
  const tf = t as T
  const lang = i18n.language
  const { site } = useClub()
  const edit = useWriteAccess('job.plan')
  const w = useWorkshopAction()
  const data = useSettingsData()
  const areas = useAreas()
  const today = useToday()
  const settings = useMemo(() => ruleSettings(tt, lang), [tt, lang])
  const [lapsText, setLapsText] = useState('')
  const [groomersText, setGroomersText] = useState('')
  const [leave, setLeave] = useState<ReadonlySet<string>>(new Set())
  const [ticks, setTicks] = useState<Readonly<Record<string, boolean>>>({})
  const [chosen, setChosen] = useState<Readonly<Record<string, string>>>({})
  const [clear, setClear] = useState(true)
  const target = job.scheduled_local_date

  const history = useMemo(() => (data ? buildHistory({
    tt, jobs: data.jobs, jobAreas: data.jobAreas, completions: data.completions, coverage: data.coverage, confirmations: data.confirmations,
    areaIds: job.areaIds, today, until: target, tz: site.timezone, readsWork: true,
  }) : null), [data, tt, job.areaIds, today, target, site.timezone])
  const proposal = useMemo(() => (history ? proposeSettings(
    { target, areaIds: job.areaIds, settings, planned: job.params, lines: history.lines },
    { leave, lapsPerWeek: limitOf(lapsText), groomersPerWeek: limitOf(groomersText) },
  ) : null), [history, target, job.areaIds, job.params, settings, leave, lapsText, groomersText])
  const frame = useMemo(() => { const byId = new Map((areas ?? []).map((a) => [a.id, a.area_type_code])); return frameFor(job.areaIds.map((id) => byId.get(id) ?? '')) }, [areas, job.areaIds])

  if (!data || !history || !proposal) return <p className="opacity-70">{t('common.loading')}</p>

  const rows = proposal.settings.filter((s) => s.picks.length > 0)
  const wordOf = (s: SettingProposal): string => { const c = chosen[s.key]; return c !== undefined && s.picks.some((p) => p.word === c) ? c : s.picks[0]!.word }
  const tickOf = (s: SettingProposal): boolean => ticks[s.key] ?? true
  const ticked: Record<string, string> = {}
  for (const s of rows) if (tickOf(s)) ticked[s.key] = wordOf(s)
  const stale = staleParams(job.params, tt, lang)
  const staleKept = stale.filter((s) => !Object.hasOwn(ticked, s.key))
  const hold = staleKept.length > 0 && !clear
  const planned = job.state === 'planned'
  const canApply = planned && edit.allowed && Object.keys(ticked).length > 0 && !hold && !w.busy && !w.pending
  const apply = () => {
    const params = applyParams(job.params, ticked, clear ? stale.map((s) => s.key) : [])
    void w.run('job.update@1', { job_id: job.id, expected_revision: job.revision, set: { params } }, 'plan.settingsPanel.applied')
  }
  const hasLap = settings.some((s) => s.family === 'lap'), hasTool = settings.some((s) => s.family === 'tool')
  const toggleLeave = (key: string, word: string) => setLeave((cur) => { const next = new Set(cur); const k = leaveKey(key, word); if (next.has(k)) next.delete(k); else next.add(k); return next })
  const plannedWord = (key: string) => { const v = job.params[key]; return typeof v === 'string' ? v : null }

  return (
    <section className="card flex flex-col gap-3" data-testid="settings-panel" aria-labelledby="settings-panel-title">
      <div className="flex items-start justify-between gap-2">
        <h2 id="settings-panel-title" className="text-xl font-bold">{t('plan.settingsPanel.title')}</h2>
        <button type="button" className="btn !px-4" onClick={onClose} data-testid="settings-close">{t('plan.settingsPanel.close')}</button>
      </div>
      <p className="text-sm" data-testid="settings-notai">{t('plan.settingsRule.notAI')}</p>
      <p className="meta" data-testid="settings-read">{readText(tf, history, plannedUntil(target))}</p>

      {(hasLap || hasTool) && (
        <fieldset className="flex flex-col gap-2" data-testid="settings-numbers">
          <legend className="text-sm font-semibold">{t('plan.settingsPanel.numbersTitle')}</legend>
          {hasLap && (
            <div className="flex flex-col gap-1">
              <label htmlFor="settings-laps" className="text-sm font-semibold">{t('plan.settingsPanel.lapsLabel')}</label>
              <input id="settings-laps" className="input sm:max-w-xs" inputMode="numeric" value={lapsText} onChange={(e) => setLapsText(e.target.value)} data-testid="settings-laps" />
              <p className="meta">{t('plan.settingsPanel.lapsHelp')}</p>
            </div>
          )}
          {hasTool && (
            <div className="flex flex-col gap-1">
              <label htmlFor="settings-groomers" className="text-sm font-semibold">{t('plan.settingsPanel.groomersLabel')}</label>
              <input id="settings-groomers" className="input sm:max-w-xs" inputMode="numeric" value={groomersText} onChange={(e) => setGroomersText(e.target.value)} data-testid="settings-groomers" />
              <p className="meta">{t('plan.settingsPanel.groomersHelp')}</p>
            </div>
          )}
        </fieldset>
      )}

      {settings.some((s) => s.choices.filter((c) => c.code !== null).length > 1) && (
        <fieldset className="flex flex-col gap-1.5" data-testid="settings-leave">
          <legend className="text-sm font-semibold">{t('plan.settingsPanel.leaveTitle')}</legend>
          <p className="meta">{t('plan.settingsPanel.leaveHelp')}</p>
          {settings.filter((s) => s.choices.filter((c) => c.code !== null).length > 1).map((s) => (
            <div key={s.key} className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-semibold">{s.label}</span>
              {s.choices.filter((c) => c.code !== null).map((c) => {
                const out = leave.has(leaveKey(s.key, c.word))
                return (
                  <button key={c.word} type="button" aria-pressed={out} onClick={() => toggleLeave(s.key, c.word)} data-testid={`settings-leave-${s.key}-${c.word}`}
                    className={`inline-flex min-h-14 items-center gap-1.5 rounded-full border-2 px-3 text-sm font-semibold ${out ? 'border-slate-300 bg-white text-slate-500 line-through dark:border-night-line dark:bg-night-card dark:text-slate-400' : 'border-brand bg-fairway-light text-brand-dark dark:border-emerald-600 dark:bg-night dark:text-fairway'}`}>
                    <LookPicture code={c.code!} frame={frame} className="size-6 shrink-0" />{c.word}
                  </button>
                )
              })}
            </div>
          ))}
        </fieldset>
      )}

      {rows.map((s) => {
        const cur = plannedWord(s.key)
        const curLook = cur ? lookForChoice(tt, s.key, cur) : null
        return (
          <fieldset key={s.key} className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3 dark:border-night-line" data-testid={`settings-row-${s.key}`}>
            <legend className="px-1 text-lg font-bold">{s.label}</legend>
            <p className="meta">
              {t('plan.settingsPanel.current')} : {curLook && <LookPicture code={curLook} frame={frame} className="mr-1 inline-block size-5 align-[-0.2em]" />}{cur ?? t('plan.settingsPanel.currentNone')}
            </p>
            <label className="flex min-h-14 items-center gap-3 font-semibold">
              <input type="checkbox" className="size-6 shrink-0 accent-brand" checked={tickOf(s)} onChange={(e) => setTicks((cur2) => ({ ...cur2, [s.key]: e.target.checked }))} data-testid={`settings-tick-${s.key}`} />
              {t('plan.settingsPanel.tick')}
            </label>
            <div role="radiogroup" aria-label={s.label} className="flex flex-col gap-1.5">
              {s.picks.map((p, i) => (
                <label key={p.word} className="flex min-h-14 items-start gap-3 rounded-xl border border-slate-200 p-3 dark:border-night-line" data-testid={`settings-pick-${s.key}-${i}`}>
                  <input type="radio" name={`settings-pick-${s.key}`} className="mt-1 size-6 shrink-0 accent-brand" checked={wordOf(s) === p.word} onChange={() => setChosen((c) => ({ ...c, [s.key]: p.word }))} />
                  <LookPicture code={p.code} frame={frame} className="size-10 shrink-0 text-brand dark:text-fairway" />
                  <span className="flex min-w-0 flex-col">
                    <span className="font-semibold">{p.word}</span>
                    <span className="meta" data-testid="settings-why">{reasonText(tf, p.reason)}</span>
                    {p.stress && <span className="meta" data-testid="settings-stress">{t('plan.settingsRule.stress')}</span>}
                  </span>
                </label>
              ))}
            </div>
            {s.setAside.length > 0 && (
              <details data-testid="settings-aside-fold">
                <summary className="min-h-14 cursor-pointer content-center text-sm font-semibold">{t('plan.settingsPanel.asideSummary', { count: s.setAside.length })}</summary>
                <ul className="mt-1 flex flex-col gap-1" data-testid="settings-aside">
                  {s.setAside.map((a) => (
                    <li key={a.word} className="meta flex items-start gap-2">
                      {a.code && <LookPicture code={a.code} frame={frame} className="mt-0.5 size-5 shrink-0" />}
                      <span><span className="font-semibold">{a.word}</span> — {asideText(tf, a, s.family)}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </fieldset>
        )
      })}
      {rows.length === 0 && <p data-testid="settings-nothing">{t('plan.settingsPanel.nothing')}</p>}

      {stale.length > 0 && (
        <Banner tone="warn" testId="settings-stale">
          <p className="font-semibold">{t('plan.settingsPanel.staleTitle')}</p>
          <ul className="mt-1 list-disc pl-5">
            {stale.map((s) => <li key={s.key}>{t(s.gone ? 'plan.settingsPanel.staleGone' : 'plan.settingsPanel.staleItem', { label: s.label, word: String(s.word) })}</li>)}
          </ul>
          <label className="mt-2 flex min-h-14 items-center gap-3 font-semibold">
            <input type="checkbox" className="size-6 shrink-0 accent-brand" checked={clear} onChange={(e) => setClear(e.target.checked)} data-testid="settings-stale-clear" />
            {t('plan.settingsPanel.staleClear')}
          </label>
          {hold && <p className="mt-1" data-testid="settings-stale-hold">{t('plan.settingsPanel.staleHold')}</p>}
          {onOpenForm && <button type="button" className="btn mt-2" onClick={onOpenForm} data-testid="settings-stale-open">{t('plan.settingsPanel.staleOpen')}</button>}
        </Banner>
      )}

      {!planned && <Banner tone="warn"><span data-testid="settings-not-planned">{t('plan.settingsPanel.notPlanned')}</span></Banner>}
      {planned && rows.length > 0 && Object.keys(ticked).length === 0 && <p className="meta">{t('plan.settingsPanel.nothingTicked')}</p>}
      <p className="text-sm" data-testid="settings-apply-sentence">{t(clear && stale.length > 0 ? 'plan.settingsPanel.applySentenceClear' : 'plan.settingsPanel.applySentence')}</p>
      {w.error && <Banner tone="error">{w.error}</Banner>}
      {w.ok && <Banner tone="ok">{w.ok}</Banner>}
      {w.pending && <PendingBanner />}
      <button type="button" className="btn btn-primary" disabled={!canApply} onClick={apply} data-testid="settings-apply">{w.busy ? t('plan.settingsPanel.applying') : t('plan.settingsPanel.apply')}</button>

      <details className="rounded-xl bg-fairway-light p-3 text-sm dark:bg-night" data-testid="settings-rule-fold">
        <summary className="min-h-14 cursor-pointer content-center font-semibold">{t('plan.settingsRule.title')}</summary>
        <ol className="mt-1 list-decimal pl-5" data-testid="settings-rule">{ruleStepKeys(settings).map((k) => <li key={k} className="py-0.5">{t(k)}</li>)}</ol>
      </details>
    </section>
  )
}
